/**
 * # Select
 *
 * A component that renders a select input with an optional border and fill wrapper.
 *
 * ## Props
 *
 * - `border` (optional): Specifies whether to display a border around the select input. Default is `false`.
 * - `fillWrapper` (optional): Specifies whether the select input should fill its wrapper container. Default is `true`.
 * - `className` (optional): Additional CSS class name(s) to apply to the select input.
 * - `children` (optional): The content to be rendered inside the select input.
 * - `...rest` (optional): Additional props to be passed to the underlying `IconInput` component.
 *
 * ## Usage
 *
 * ```tsx
 * import Select from './Select.react';
 *
 * const MyComponent = () => {
 *   return (
 *     <Select
 *       border={true}
 *       fillWrapper={false}
 *       className="custom-select"
 *       placeholder="Select an option"
 *     >
 *       <option value="option1">Option 1</option>
 *       <option value="option2">Option 2</option>
 *       <option value="option3">Option 3</option>
 *     </Select>
 *   );
 * };
 * ```
 */

import React, {forwardRef} from 'react';
import classnames from 'classnames';

import type {BaseIconInputProps} from '../IconInput/IconInput.react';
import IconInput from '../IconInput/IconInput.react';

type SelectProps = {
  border?: boolean;
  fillWrapper?: boolean;
} & BaseIconInputProps &
  React.SelectHTMLAttributes<HTMLSelectElement> &
  PropsWithChildrenOptional &
  PropsWithClassNameOptional;

// Cast IconInput to a concrete type so TypeScript doesn't resolve
// PolymorphicComponentProps<'select', ...> on every usage (~590s hotspot).
// Props are already validated at Select's forwardRef boundary via SelectProps.
const ConcreteIconInput: React.FC<
  SelectProps & {as: string; inputRef: React.Ref<HTMLSelectElement>}
> = IconInput as any;

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({className, fillWrapper = true, ...rest}, ref) => (
    <ConcreteIconInput
      as='select'
      fillWrapper={fillWrapper}
      className={classnames('m-select', className)}
      iconRight='caret-down'
      inputRef={ref}
      {...rest}
    />
  )
);

Select.displayName = 'Select';

export default Select;
